import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  BuildingOfficeIcon,
  ChartBarIcon,
  TrophyIcon,
  StarIcon,
  MapPinIcon,
  UsersIcon,
  AcademicCapIcon,
  CurrencyRupeeIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import StandardNavbar from './StandardNavbar';

const CollegePredictor = () => {
  const { user } = useAuth();
  const [targetCollege, setTargetCollege] = useState('VJTI');
  const [targetBranch, setTargetBranch] = useState('Computer Engineering');
  const [gapToTarget, setGapToTarget] = useState(null);
  const [userPerformance, setUserPerformance] = useState({
    mathematics: 75,
    physics: 70,
    chemistry: 65
  });
  const [predictedRank, setPredictedRank] = useState(null);
  const [collegePredictions, setCollegePredictions] = useState([]);
  const [selectedCollege, setSelectedCollege] = useState(null);
  const [showDetailedAnalysis, setShowDetailedAnalysis] = useState(false);
  // New predictor inputs (sticky bar)
  const location = useLocation();
  const [percentile, setPercentile] = useState(92.0);
  const [category, setCategory] = useState('OPEN');
  const [domicile, setDomicile] = useState('MH');
  const [homeRegion, setHomeRegion] = useState('Mumbai');
  const [huOu, setHuOu] = useState('HU');
  const [round, setRound] = useState('R2');
  const [gender, setGender] = useState('Any');
  const [branchPrefs, setBranchPrefs] = useState(['CSE','IT','E&TC','AI&DS']);
  const [cityFilter, setCityFilter] = useState('Any');
  const [feesMax, setFeesMax] = useState(200000);
  const [hostel, setHostel] = useState(false);
  const [shortlist, setShortlist] = useState([]);
  const [showShortlistMobile, setShowShortlistMobile] = useState(false);
  const [savedSets, setSavedSets] = useState([]);
  const [selectedSet, setSelectedSet] = useState('');

  // College database with cut-offs and details
  const colleges = [
    {
      id: 'vjti',
      name: 'Veermata Jijabai Technological Institute (VJTI)',
      location: 'Mumbai',
      tier: 'Tier 1',
      cutOffRank: 8000,
      branches: [
        { name: 'Computer Engineering', seats: 120, cutOff: 5000 },
        { name: 'Mechanical Engineering', seats: 120, cutOff: 12000 },
        { name: 'Electrical Engineering', seats: 120, cutOff: 10000 },
        { name: 'Civil Engineering', seats: 120, cutOff: 15000 }
      ],
      rating: 4.8,
      placement: {
        average: 850000,
        highest: 4500000,
        topRecruiters: ['Google', 'Microsoft', 'Amazon', 'TCS']
      },
      campusLife: 'Excellent campus with modern facilities and vibrant student life',
      admissionChance: 0
    },
    {
      id: 'coep',
      name: 'College of Engineering, Pune (COEP)',
      location: 'Pune',
      tier: 'Tier 1',
      cutOffRank: 12000,
      branches: [
        { name: 'Computer Engineering', seats: 120, cutOff: 8000 },
        { name: 'Mechanical Engineering', seats: 120, cutOff: 15000 },
        { name: 'Electrical Engineering', seats: 120, cutOff: 13000 },
        { name: 'Civil Engineering', seats: 120, cutOff: 18000 }
      ],
      rating: 4.7,
      placement: {
        average: 800000,
        highest: 4000000,
        topRecruiters: ['Infosys', 'Wipro', 'Cognizant', 'Accenture']
      },
      campusLife: 'Historic campus with strong academic traditions and excellent facilities',
      admissionChance: 0
    },
    {
      id: 'spit',
      name: 'Sardar Patel Institute of Technology (SPIT)',
      location: 'Mumbai',
      tier: 'Tier 1',
      cutOffRank: 15000,
      branches: [
        { name: 'Computer Engineering', seats: 120, cutOff: 10000 },
        { name: 'Information Technology', seats: 120, cutOff: 12000 },
        { name: 'Electronics & Telecommunication', seats: 120, cutOff: 14000 },
        { name: 'Mechanical Engineering', seats: 120, cutOff: 18000 }
      ],
      rating: 4.6,
      placement: {
        average: 750000,
        highest: 3500000,
        topRecruiters: ['Tech Mahindra', 'L&T', 'Capgemini', 'Mindtree']
      },
      campusLife: 'Modern campus with focus on innovation and industry collaboration',
      admissionChance: 0
    },
    {
      id: 'pict',
      name: 'Pune Institute of Computer Technology (PICT)',
      location: 'Pune',
      tier: 'Tier 2',
      cutOffRank: 25000,
      branches: [
        { name: 'Computer Engineering', seats: 120, cutOff: 18000 },
        { name: 'Information Technology', seats: 120, cutOff: 20000 },
        { name: 'Electronics & Telecommunication', seats: 120, cutOff: 22000 }
      ],
      rating: 4.5,
      placement: {
        average: 700000,
        highest: 3000000,
        topRecruiters: ['Persistent', 'KPIT', 'Zensar', 'Birlasoft']
      },
      campusLife: 'Student-friendly campus with good infrastructure and placement support',
      admissionChance: 0
    },
    {
      id: 'vit',
      name: 'Vishwakarma Institute of Technology (VIT)',
      location: 'Pune',
      tier: 'Tier 2',
      cutOffRank: 30000,
      branches: [
        { name: 'Computer Engineering', seats: 120, cutOff: 22000 },
        { name: 'Mechanical Engineering', seats: 120, cutOff: 28000 },
        { name: 'Civil Engineering', seats: 120, cutOff: 32000 }
      ],
      rating: 4.4,
      placement: {
        average: 650000,
        highest: 2800000,
        topRecruiters: ['L&T', 'Godrej', 'Mahindra', 'Tata Motors']
      },
      campusLife: 'Spacious campus with good academic environment and industry connections',
      admissionChance: 0
    },
    {
      id: 'dypatil',
      name: 'DY Patil College of Engineering',
      location: 'Pune',
      tier: 'Tier 2',
      cutOffRank: 40000,
      branches: [
        { name: 'Computer Engineering', seats: 120, cutOff: 30000 },
        { name: 'Mechanical Engineering', seats: 120, cutOff: 35000 },
        { name: 'Civil Engineering', seats: 120, cutOff: 40000 }
      ],
      rating: 4.3,
      placement: {
        average: 600000,
        highest: 2500000,
        topRecruiters: ['HCL', 'Tech Mahindra', 'Infosys', 'Wipro']
      },
      campusLife: 'Good infrastructure with focus on practical learning and placements',
      admissionChance: 0
    }
  ];

  // Helpers
  const rankToPercentile = (rank) => {
    const total = 150000; // rough candidates
    return Math.max(0, Math.min(100, 100 * (1 - (rank / total))));
  };

  const pctToMarks = (pct) => Math.max(0, Math.min(200, Math.round((pct/100) * 200)));

  // Rank prediction algorithm (kept for left panel preview)
  const calculatePredictedRank = (performance) => {
    const { mathematics, physics, chemistry } = performance;
    
    // Weighted average based on CET weightage (Maths: 50, Physics: 50, Chemistry: 50)
    const weightedScore = (mathematics * 0.4 + physics * 0.3 + chemistry * 0.3);
    
    // Convert score to rank (simplified algorithm)
    // In real implementation, this would use historical data and ML models
    const baseRank = 50000; // Base rank for average performance
    const scoreMultiplier = 2.5; // How much score affects rank
    
    const predictedRank = Math.max(1, Math.round(baseRank - (weightedScore - 50) * scoreMultiplier * 1000));
    
    return predictedRank;
  };

  // Calculate college admission chances
  const calculateAdmissionChances = (rank) => {
    // New probability bands via percentile gap
    const mu = 0.0, sigma = 0.6;
    const results = colleges.map(college => {
      let branchChances = college.branches.map(branch => {
        const cutoffPct = rankToPercentile(branch.cutOff);
        // Adjustments for category / round / HU-OU (v1 heuristics)
        let adj = 0;
        if (category === 'EWS' || category === 'OBC') adj -= 0.3;
        if (category === 'SC' || category === 'ST' || category === 'VJ/NT') adj -= 1.0;
        if (category === 'TFWS') adj += 0.5; // stricter
        if (huOu === 'HU') adj += 0.2; else if (huOu === 'OU') adj -= 0.2;
        if (round === 'R1') adj += 0.3; else if (round === 'R3') adj -= 0.2;
        const cutoffAdj = cutoffPct + adj;
        const gap = percentile - cutoffAdj; // trend_adj applied
        const p = 1 / (1 + Math.exp(-(gap - mu)/sigma));
        const tier = p >= 0.8 ? 'Likely' : p >= 0.5 ? 'Competitive' : 'Reach';
        const band = tier === 'Likely' ? '80–100%' : '50–80%';
        const deltaPct = Math.max(0, (cutoffAdj - percentile));
        const deltaMarks = Math.max(0, pctToMarks(cutoffAdj) - pctToMarks(percentile));
        // Branch labels mapping for chip matching
        const bname = branch.name.toLowerCase();
        const label = bname.includes('computer') ? 'CSE'
          : bname.includes('information') ? 'IT'
          : bname.includes('telecommunication') || bname.includes('electronics') ? 'E&TC'
          : bname.includes('ai') || bname.includes('data') ? 'AI&DS'
          : bname.includes('mechanical') ? 'Mechanical'
          : bname.includes('electrical') ? 'Electrical'
          : bname.includes('civil') ? 'Civil' : 'Other';
        return {
          name: branch.name,
          p,
          tier,
          band,
          cutoffPct: Number(cutoffAdj.toFixed(1)),
          seats: branch.seats,
          deltaPct: Number(deltaPct.toFixed(1)),
          deltaMarks,
          label
        };
      });
      // Filter by branch preferences if any selected
      const activePrefs = branchPrefs && branchPrefs.length ? branchPrefs : [];
      if (activePrefs.length > 0) {
        branchChances = branchChances.filter(b => activePrefs.includes(b.label) || (b.label==='Other' && activePrefs.includes('Other')));
      }
      // Sort by probability then by preference order
      const prefOrder = (x) => {
        const idx = activePrefs.indexOf(x.label);
        return idx === -1 ? 999 : idx;
      };
      branchChances = branchChances.sort((a,b)=> (b.p - a.p) || (prefOrder(a) - prefOrder(b)));
      const bestP = branchChances.length ? branchChances[0].p : 0;
      return { ...college, prob: bestP, branchChances };
    });
    return results
      .filter(c => (cityFilter==='Any' || c.location===cityFilter))
      .sort((a,b)=> b.prob - a.prob);
  };

  useEffect(() => {
    if (userPerformance) {
      // Prefill percentile from state or saved filters
      try {
        const st = location?.state || {};
        if (st.percentile) setPercentile(st.percentile);
        if (st.confidence) localStorage.setItem('predictor_confidence', String(st.confidence));
        const saved = JSON.parse(localStorage.getItem('college_filters_v1') || 'null');
        if (saved) {
          setCategory(saved.category || 'OPEN');
          setHomeRegion(saved.homeRegion || 'Mumbai');
          setHuOu(saved.huOu || 'HU');
          setRound(saved.round || 'R2');
          setCityFilter(saved.cityFilter || 'Any');
          setFeesMax(saved.feesMax || 200000);
        }
        const sets = JSON.parse(localStorage.getItem('college_filters_sets_v1') || '[]');
        setSavedSets(Array.isArray(sets)?sets:[]);
        const savedSL = JSON.parse(localStorage.getItem('shortlist_v1') || '[]');
        if (Array.isArray(savedSL)) setShortlist(savedSL);
      } catch {}
      const rank = calculatePredictedRank(userPerformance);
      setPredictedRank(rank);
      
      const predictions = calculateAdmissionChances(rank);
      setCollegePredictions(predictions);

      // Gap to target
      const target = predictions.find(c => c.name.includes(targetCollege));
      if (target) {
        const branch = target.branchChances.find(b => b.name === targetBranch) || target.branchChances[0];
        const gap = Math.max(0, (branch.cutOff || 0) - rank);
        setGapToTarget({ college: target.name, branch: branch.name, rankGap: gap, chance: target.admissionChance });
      } else {
        setGapToTarget(null);
      }
    }
  }, [userPerformance]);

  const handlePerformanceChange = (subject, value) => {
    setUserPerformance(prev => ({
      ...prev,
      [subject]: parseInt(value)
    }));
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'Tier 1': return 'bg-blue-100 text-blue-800';
      case 'Tier 2': return 'bg-green-100 text-green-800';
      case 'Tier 3': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const tierColor = (tier) => tier==='Likely' ? 'bg-green-50 text-green-700 border border-green-200' : tier==='Competitive' ? 'bg-yellow-50 text-yellow-700 border border-yellow-200' : 'bg-red-50 text-red-700 border border-red-200';

  const confidenceChip = (college) => {
    const dataCompleteness = 0.7; // assume last year R2 available
    const seatMatrixQuality = college?.branches?.length ? 0.8 : 0.5;
    const predictorConf = parseFloat(localStorage.getItem('predictor_confidence') || '0.5');
    const conf = Math.max(0, Math.min(1, 0.33*dataCompleteness + 0.33*seatMatrixQuality + 0.34*predictorConf));
    const label = conf < 0.33 ? 'Low' : conf < 0.66 ? 'Medium' : 'High';
    return <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700 border">Confidence: {label}</span>;
  };

  const addToShortlist = (item) => {
    setShortlist(prev => {
      if (prev.find(p => p.id===item.id)) return prev;
      const next = [...prev, item];
      try { localStorage.setItem('shortlist_v1', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const exportCSV = () => {
    const rows = [['Preference','College','Branch','Tier']].concat(shortlist.map((c,i)=>[i+1,c.name,c.branch,c.tier]));
    const csv = rows.map(r=>r.map(f=>`"${String(f).replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csv],{type:'text/csv;charset=utf-8;'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href=url; a.download='shortlist.csv'; a.click(); URL.revokeObjectURL(url);
  };

  const toggleBranchPref = (chip) => {
    setBranchPrefs(prev => {
      const set = new Set(prev);
      if (set.has(chip)) set.delete(chip); else set.add(chip);
      return Array.from(set);
    });
  };

  const saveFilterSet = () => {
    const name = prompt('Save filters as (name):');
    if (!name) return;
    const payload = { name, percentile, category, homeRegion, huOu, round, cityFilter, feesMax, branchPrefs };
    try {
      const sets = JSON.parse(localStorage.getItem('college_filters_sets_v1') || '[]');
      const filtered = sets.filter(s => s.name !== name);
      filtered.push(payload);
      localStorage.setItem('college_filters_sets_v1', JSON.stringify(filtered));
      setSavedSets(filtered);
      setSelectedSet(name);
      toast.success('Saved');
    } catch {}
  };

  const loadFilterSet = (name) => {
    try {
      const sets = JSON.parse(localStorage.getItem('college_filters_sets_v1') || '[]');
      const s = sets.find(x => x.name === name);
      if (!s) return;
      setSelectedSet(name);
      setPercentile(s.percentile); setCategory(s.category); setHomeRegion(s.homeRegion); setHuOu(s.huOu); setRound(s.round); setCityFilter(s.cityFilter); setFeesMax(s.feesMax); setBranchPrefs(s.branchPrefs || []);
      toast.success('Loaded');
    } catch {}
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <StandardNavbar title="College Predictor" showBack={true} showHome={true} />
      {/* Sticky filter bar */}
      <div className="bg-white/95 border-b sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-7 gap-2 items-center">
          <div className="flex items-center gap-2 col-span-1">
            <label className="text-xs text-gray-600">Percentile</label>
            <input value={percentile} onChange={(e)=>setPercentile(parseFloat(e.target.value||'0'))} type="number" step="0.1" className="w-full max-w-[6rem] border rounded px-2 py-1" />
          </div>
          <select value={category} onChange={(e)=>setCategory(e.target.value)} className="border rounded px-2 py-1 text-sm w-full">
            {['OPEN','EWS','OBC','SC','ST','VJ/NT','TFWS'].map(o=> <option key={o}>{o}</option>)}
          </select>
          <select value={homeRegion} onChange={(e)=>setHomeRegion(e.target.value)} className="border rounded px-2 py-1 text-sm w-full">
            {['Mumbai','Pune','Nagpur','Aurangabad'].map(o=> <option key={o}>{o}</option>)}
          </select>
          <select value={huOu} onChange={(e)=>setHuOu(e.target.value)} className="border rounded px-2 py-1 text-sm w-full">
            {['HU','OU'].map(o=> <option key={o}>{o}</option>)}
          </select>
          <select value={round} onChange={(e)=>setRound(e.target.value)} className="border rounded px-2 py-1 text-sm w-full">
            {['R1','R2','R3'].map(o=> <option key={o}>{o}</option>)}
          </select>
          <select value={cityFilter} onChange={(e)=>setCityFilter(e.target.value)} className="border rounded px-2 py-1 text-sm w-full">
            {['Any','Mumbai','Pune'].map(o=> <option key={o}>{o}</option>)}
          </select>
          <div className="flex items-center gap-2 col-span-1">
            <label className="text-xs text-gray-600">Fees ≤</label>
            <input value={feesMax} onChange={(e)=>setFeesMax(parseInt(e.target.value||'0',10))} type="number" className="w-full max-w-[7rem] border rounded px-2 py-1" />
          </div>
          <div className="flex items-center justify-end gap-2 col-span-2 sm:col-span-1">
            <select value={selectedSet} onChange={(e)=> loadFilterSet(e.target.value)} className="border rounded px-2 py-1 text-sm w-full">
              <option value="">Saved…</option>
              {savedSets.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
            </select>
            <button onClick={saveFilterSet} className="px-3 py-1 rounded border text-sm">Save</button>
            <button onClick={()=>{ try{ localStorage.setItem('college_filters_v1', JSON.stringify({ percentile, category, homeRegion, huOu, round, cityFilter, feesMax })); } catch {}; toast.success('Filters saved'); }} className="px-3 py-1 rounded bg-blue-600 text-white text-sm">Apply</button>
          </div>
        </div>
      </div>
      {/* Branch preference chips */}
      <div className="bg-white/80 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 overflow-x-auto">
          <div className="flex gap-2 text-sm whitespace-nowrap">
            {['CSE','IT','E&TC','AI&DS','Mechanical','Electrical','Civil','Other'].map(ch => (
              <button key={ch} onClick={()=>toggleBranchPref(ch)} className={`${branchPrefs.includes(ch)?'bg-blue-600 text-white':'bg-gray-100 text-gray-700'} px-3 py-1 rounded-full border`}>{ch}</button>
            ))}
            <span className="text-xs text-gray-500 ml-2">Tip: Select branches you prefer to prioritize results.</span>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <p className="text-xs text-gray-600 mb-4">Predictions use 2024 CAP closing percentiles by round, category and HU/OU with your latest percentile. We show a probability band (Likely / Competitive / Reach) and our confidence.</p>
 
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Performance Input */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Your Performance</h2>
              {/* Target selector */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Target College</label>
                <select value={targetCollege} onChange={(e) => setTargetCollege(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2">
                  {colleges.map(c => (<option key={c.id} value={c.name.includes('VJTI') ? 'VJTI' : c.name.split('(')[0].trim()}>{c.name}</option>))}
                </select>
                <label className="block text-sm font-medium text-gray-700 mt-4 mb-2">Target Branch</label>
                <select value={targetBranch} onChange={(e) => setTargetBranch(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2">
                  {colleges[0].branches.map(b => (<option key={b.name} value={b.name}>{b.name}</option>))}
                </select>
              </div>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mathematics Accuracy (%)
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={userPerformance.mathematics}
                    onChange={(e) => handlePerformanceChange('mathematics', e.target.value)}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-sm text-gray-600 mt-1">
                    <span>0%</span>
                    <span className="font-semibold">{userPerformance.mathematics}%</span>
                    <span>100%</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Physics Accuracy (%)
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={userPerformance.physics}
                    onChange={(e) => handlePerformanceChange('physics', e.target.value)}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-sm text-gray-600 mt-1">
                    <span>0%</span>
                    <span className="font-semibold">{userPerformance.physics}%</span>
                    <span>100%</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Chemistry Accuracy (%)
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={userPerformance.chemistry}
                    onChange={(e) => handlePerformanceChange('chemistry', e.target.value)}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-sm text-gray-600 mt-1">
                    <span>0%</span>
                    <span className="font-semibold">{userPerformance.chemistry}%</span>
                    <span>100%</span>
                  </div>
                </div>
              </div>

              {/* Predicted Rank */}
              {predictedRank && (
                <div className="mt-8 p-4 bg-blue-50 rounded-lg">
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">Predicted CET Rank</h3>
                  <div className="text-3xl font-bold text-blue-600 mb-2">
                    {predictedRank.toLocaleString()}
                  </div>
                  <p className="text-sm text-blue-700">
                    Based on your current performance across all subjects
                  </p>
                </div>
              )}

              {gapToTarget && (
                <div className="mt-4 p-4 bg-green-50 rounded-lg">
                  <h3 className="text-lg font-semibold text-green-900 mb-2">Distance to Target</h3>
                  <p className="text-green-800 text-sm">
                    {gapToTarget.rankGap === 0
                      ? `You are within the cutoff for ${gapToTarget.college} (${gapToTarget.branch}).`
                      : `You are ${gapToTarget.rankGap.toLocaleString()} ranks away from ${gapToTarget.college} (${gapToTarget.branch}).`}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* College Predictions */}
          <div className="lg:col-span-2">
            <div className="space-y-6">
              {collegePredictions.map((college) => (
                <div key={college.id} className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-xl font-semibold text-gray-900">{college.name}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTierColor(college.tier)}`}>
                          {college.tier}
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                        <div className="flex items-center">
                          <MapPinIcon className="h-4 w-4 mr-1" />
                          {college.location}
                        </div>
                        <div className="flex items-center">
                          <StarIcon className="h-4 w-4 mr-1" />
                          {college.rating}/5
                        </div>
                        <div className="flex items-center">
                          <CurrencyRupeeIcon className="h-4 w-4 mr-1" />
                          {college.placement.average.toLocaleString()}/year
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-right space-y-1">
                      <div className={`inline-block px-2 py-1 rounded ${tierColor(college.branchChances[0]?.tier || 'Reach')}`}>
                        {(college.branchChances[0]?.tier)||'Reach'} {college.branchChances[0]?.tier==='Likely'?'80–100%':college.branchChances[0]?.tier==='Competitive'?'50–80%':'≤50%'}
                      </div>
                      <div>{confidenceChip(college)}</div>
                    </div>
                  </div>

                  {/* Branch Details */}
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Available Branches</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {college.branchChances.map((branch, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <div className="font-medium text-gray-900">{branch.name}</div>
                            <div className="text-sm text-gray-600">Cut-off (2024 R2, OPEN-HU): <span className="font-medium">{branch.cutoffPct}</span></div>
                            <div className="text-xs text-gray-600">You are {branch.deltaPct>0?`−${branch.deltaPct}`:'+0.0'} percentile away → {branch.deltaMarks>0?`+${branch.deltaMarks}`:'+0'} marks needed</div>
                          </div>
                          <div className="text-right">
                            <div className={`font-semibold`}>{branch.tier}</div>
                            <div className="text-xs text-gray-600">
                              {branch.seats} seats
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => setSelectedCollege(college)}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center"
                    >
                      View Details
                      <ArrowRightIcon className="h-4 w-4 ml-1" />
                    </button>
                    <button onClick={()=> addToShortlist({ id: college.id, name: college.name, branch: college.branchChances[0]?.name || 'CSE', tier: college.tier })} className="px-3 py-1 rounded border text-sm">Add to Preference</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Shortlist drawer (desktop) */}
        {shortlist.length>0 && (
          <div className="hidden lg:block fixed right-4 top-24 w-72 bg-white rounded-lg shadow-lg border p-4 max-h-[70vh] overflow-auto">
            <div className="font-semibold text-gray-900 mb-2">Your Preference List</div>
            <ol className="text-sm space-y-1">
              {shortlist.map((s,i)=> (
                <li key={s.id} className="flex items-center justify-between gap-2">
                  <span className="truncate"><span className="text-gray-500 mr-1">{i+1}.</span>{s.name} — {s.branch}</span>
                  <div className="flex gap-1">
                    <button onClick={()=> setShortlist(prev => { const next=[...prev]; if(i>0){ [next[i-1],next[i]]=[next[i],next[i-1]]; } try{ localStorage.setItem('shortlist_v1', JSON.stringify(next)); } catch {}; return next; })} className="text-xs px-1 border rounded">↑</button>
                    <button onClick={()=> setShortlist(prev => { const next=[...prev]; if(i<next.length-1){ [next[i+1],next[i]]=[next[i],next[i+1]]; } try{ localStorage.setItem('shortlist_v1', JSON.stringify(next)); } catch {}; return next; })} className="text-xs px-1 border rounded">↓</button>
                    <button onClick={()=> setShortlist(prev => { const next=prev.filter(p=>p.id!==s.id); try{ localStorage.setItem('shortlist_v1', JSON.stringify(next)); } catch {}; return next; })} className="text-xs text-red-600">✕</button>
                  </div>
                </li>
              ))}
            </ol>
            <button onClick={exportCSV} className="mt-3 w-full px-3 py-2 rounded bg-blue-600 text-white text-sm">Export Preference List</button>
          </div>
        )}

        {/* Mobile shortlist button + sheet */}
        <button onClick={()=> setShowShortlistMobile(true)} className="lg:hidden fixed bottom-4 right-4 px-4 py-2 rounded-full shadow bg-blue-600 text-white">Shortlist ({shortlist.length})</button>
        {showShortlistMobile && (
          <div className="lg:hidden fixed inset-0 bg-black/40 z-40" onClick={()=>setShowShortlistMobile(false)}>
            <div className="absolute left-0 right-0 bottom-0 bg-white rounded-t-xl p-4 max-h-[70vh] overflow-auto" onClick={(e)=>e.stopPropagation()}>
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold text-gray-900">Your Preference List</div>
                <button onClick={()=>setShowShortlistMobile(false)} className="text-gray-600">Close</button>
              </div>
              {shortlist.length===0 && <div className="text-sm text-gray-600">Add colleges to build your order.</div>}
              <ol className="text-sm space-y-1">
                {shortlist.map((s,i)=> (
                  <li key={s.id} className="flex items-center justify-between gap-2">
                    <span className="truncate"><span className="text-gray-500 mr-1">{i+1}.</span>{s.name} — {s.branch}</span>
                    <div className="flex gap-1">
                      <button onClick={()=> setShortlist(prev => { const next=[...prev]; if(i>0){ [next[i-1],next[i]]=[next[i],next[i-1]]; } try{ localStorage.setItem('shortlist_v1', JSON.stringify(next)); } catch {}; return next; })} className="text-xs px-1 border rounded">↑</button>
                      <button onClick={()=> setShortlist(prev => { const next=[...prev]; if(i<next.length-1){ [next[i+1],next[i]]=[next[i],next[i+1]]; } try{ localStorage.setItem('shortlist_v1', JSON.stringify(next)); } catch {}; return next; })} className="text-xs px-1 border rounded">↓</button>
                      <button onClick={()=> setShortlist(prev => { const next=prev.filter(p=>p.id!==s.id); try{ localStorage.setItem('shortlist_v1', JSON.stringify(next)); } catch {}; return next; })} className="text-xs text-red-600">✕</button>
                    </div>
                  </li>
                ))}
              </ol>
              {shortlist.length>0 && (
                <button onClick={exportCSV} className="mt-3 w-full px-3 py-2 rounded bg-blue-600 text-white text-sm">Export Preference List</button>
              )}
            </div>
          </div>
        )}

        {/* College Detail Modal */}
        {selectedCollege && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">{selectedCollege.name}</h2>
                  <button
                    onClick={() => setSelectedCollege(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* College Info */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">College Information</h3>
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <MapPinIcon className="h-5 w-5 text-gray-400 mr-3" />
                        <span className="text-gray-700">{selectedCollege.location}</span>
                      </div>
                      <div className="flex items-center">
                        <StarIcon className="h-5 w-5 text-gray-400 mr-3" />
                        <span className="text-gray-700">{selectedCollege.rating}/5 Rating</span>
                      </div>
                      <div className="flex items-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTierColor(selectedCollege.tier)}`}>
                          {selectedCollege.tier}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Placement Info */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Placement Statistics</h3>
                    <div className="space-y-3">
                      <div>
                        <div className="text-sm text-gray-600">Average Package</div>
                        <div className="font-semibold text-gray-900">
                          ₹{selectedCollege.placement.average.toLocaleString()}/year
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">Highest Package</div>
                        <div className="font-semibold text-gray-900">
                          ₹{selectedCollege.placement.highest.toLocaleString()}/year
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">Top Recruiters</div>
                        <div className="text-gray-900">
                          {selectedCollege.placement.topRecruiters.join(', ')}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Campus Life */}
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Campus Life</h3>
                  <p className="text-gray-700">{selectedCollege.campusLife}</p>
                </div>

                {/* Admission Guidance */}
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Admission Guidance</h3>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="flex items-start">
                      <AcademicCapIcon className="h-5 w-5 text-blue-600 mr-3 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-blue-900 mb-2">How to improve your chances:</h4>
                        <ul className="text-sm text-blue-800 space-y-1">
                          <li>• Focus on improving your weakest subject</li>
                          <li>• Practice more questions from previous years</li>
                          <li>• Take regular mock tests to track progress</li>
                          <li>• Consider multiple branch preferences</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CollegePredictor;
